// context/CartContext.js
import { createContext, useContext, useReducer, useEffect } from "react";
import { addToCartProduct, clearCartProduct, getCart, removeCartItem } from "../api/cart/cartApi";
import { useAuth } from "./auth";
import { useToast } from "./Toast/useToast";

const CartContext = createContext();
const initialState = {
  cartItems: [],
};

const cartReducer = (state, action) => {
  let updatedCart;
  switch (action.type) {
    case "SET_CART":
      updatedCart = action.payload || [];
      localStorage.setItem("cart", JSON.stringify(updatedCart));
      return { ...state, cartItems: updatedCart };

    case "ADD_TO_CART":
      updatedCart = [...state.cartItems, action.payload];
      localStorage.setItem("cart", JSON.stringify(updatedCart));
      return { ...state, cartItems: updatedCart };

    case "REMOVE_FROM_CART":
      updatedCart = state.cartItems.filter(item => item._id !== action.payload);
      localStorage.setItem("cart", JSON.stringify(updatedCart));
      return { ...state, cartItems: updatedCart };

    case "CLEAR_CART":
      localStorage.removeItem("cart");
      return { ...state, cartItems: [] };

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [auth] = useAuth();
  const toast = useToast();

  const userId = auth?.user?.id;

  // Load cart from backend
  const loadCart = async () => {
    try {
      const res = await getCart(userId);
      dispatch({ type: "SET_CART", payload: res?.data?.items });
    } catch (err) {
      console.error("Failed to load cart", err);
      // Fallback to localStorage if backend fails
      const localData = localStorage.getItem("cart");
      if (localData) {
        dispatch({ type: "SET_CART", payload: JSON.parse(localData) });
      }
    }
  };

  // Add to cart (Backend + Local)
  const addToCart = async (item) => {
    try {
      const data = {
        userId,
        planType: item.planType,
        price: item.price,
        currency: item.currency,
        serviceName: item.serviceName,
        serviceImage: item.serviceImage,
        features: item.features,
        quantity: item.quantity,
      };
      const res = await addToCartProduct(data);
      // Assuming your API returns { success: true/false, message, cart }
      if (res.data.success) {
        dispatch({ type: "SET_CART", payload: res.data.cart.items });
        toast.success(res.data.message || "Item added to cart");
      } else {
        toast.error(res.data.message || "Something went wrong");
      }
    } catch (err) {
      console.error("Failed to add to cart", err);
      toast.error(err.response?.data?.message || err.message || "Failed to add to cart");
    }
  };

  // Remove from cart
  const removeFromCart = async (itemId) => {
    try {
      const res = await removeCartItem(userId, itemId);
      dispatch({ type: "SET_CART", payload: res.data.items });
    } catch (err) {
      console.error("Failed to remove item", err);
    }
  };

  // Clear cart
  const clearCart = async () => {
    try {
      await clearCartProduct(userId);
      dispatch({ type: "CLEAR_CART" });
    } catch (err) {
      console.error("Failed to clear cart", err);
    }
  };

  useEffect(() => {
    if (userId) {
      loadCart();
    } else {
      // Load from localStorage if user is not logged in
      const localData = localStorage.getItem("cart");
      if (localData) {
        dispatch({ type: "SET_CART", payload: JSON.parse(localData) });
      }
    }
  }, [userId]);

  return (
    <CartContext.Provider
      value={{
        cart: state.cartItems,
        addToCart,
        removeFromCart,
        clearCart,
        loadCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within a CartProvider");
  return context;
};
